import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutData } from '@/mocks/articles';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormSuccess(true);
      form.reset();
      return;
    }

    setSubmitting(true);
    try {
      formData.delete('phone_alt');
      const response = await fetch('http://cefotiam.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && (parsed as { code?: string }).code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        const serverMsg =
          (parsed as { meta?: { message?: string; detail?: string } }).meta?.message ||
          (parsed as { meta?: { message?: string; detail?: string } }).meta?.detail ||
          (parsed as { message?: string }).message ||
          responseText ||
          '提交失败';
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 2026世界杯官网 ZL5.net',
    description: aboutData.brandIntro.description,
    url: 'http://cefotiam.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '2026世界杯官网',
      url: 'http://cefotiam.com/',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://cefotiam.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://cefotiam.com/about' },
      ],
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '2026世界杯官网 ZL5.net',
    url: 'http://cefotiam.com/',
    description: aboutData.brandIntro.description,
    email: aboutData.contact.email,
    telephone: aboutData.contact.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutData.contact.address,
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'business cooperation',
      email: aboutData.contact.email,
      telephone: aboutData.contact.phone,
      availableLanguage: 'Chinese',
    },
  };

  return (
    <>
      <Navbar />
      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
        />

        <section className="pt-24 md:pt-28 pb-16 md:pb-20 bg-background-100">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <nav aria-label="breadcrumb" className="mb-6">
              <ol className="flex items-center gap-1.5 text-sm">
                <li>
                  <Link to="/" className="text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">
                    首页
                  </Link>
                </li>
                <li>
                  <span className="text-foreground-400 mx-1">/</span>
                </li>
                <li>
                  <span className="text-foreground-800 font-medium">关于我们</span>
                </li>
              </ol>
            </nav>

            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
                {aboutData.brandIntro.subtitle}
              </span>
              <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-5">
                {aboutData.brandIntro.title}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                {aboutData.brandIntro.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-16">
              {aboutData.brandIntro.values.map((v) => (
                <div
                  key={v.label}
                  className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center transition-all duration-300 hover:border-primary-300/50"
                >
                  <h3 className="font-heading font-bold text-lg text-foreground-900 mb-2">{v.label}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <p className="text-xl md:text-2xl font-heading font-semibold text-primary-600 italic">
                &ldquo;{aboutData.brandIntro.mission}&rdquo;
              </p>
            </div>
          </div>
        </section>

        <section id="reputation" className="py-16 md:py-24 bg-background-50">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
                实力认证
              </span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 mb-3">
                {aboutData.reputation.title}
              </h2>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-14">
              {aboutData.reputation.items.map((item) => (
                <div
                  key={item.label}
                  className="flex flex-col items-center text-center p-6 md:p-8 bg-background-100 rounded-xl border border-background-200/70"
                >
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <span className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                    {item.value}
                  </span>
                  <span className="text-sm text-foreground-500 mt-1">{item.suffix}</span>
                  <span className="text-xs text-foreground-400 mt-1">{item.label}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {aboutData.reputation.certifications.map((cert) => (
                <div
                  key={cert.name}
                  className="flex items-start gap-3 p-5 bg-background-50 rounded-xl border border-background-200/70"
                >
                  <span className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-secondary-100 text-secondary-600">
                    <i className="ri-verified-badge-line text-lg"></i>
                  </span>
                  <div>
                    <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-1">
                      {cert.name}
                    </h4>
                    <p className="text-xs text-foreground-500">{cert.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-16 md:py-24 bg-background-100">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
                合作共赢
              </span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 mb-3">
                {aboutData.contact.title}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                {aboutData.contact.subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 max-w-5xl mx-auto">
              <div className="lg:col-span-2 flex flex-col gap-6">
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-5">
                    联系方式
                  </h3>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-primary-100 text-primary-600">
                        <i className="ri-mail-line text-lg"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500 mb-0.5">电子邮箱</p>
                        <p className="text-sm font-medium text-foreground-800">{aboutData.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-accent-100 text-accent-600">
                        <i className="ri-phone-line text-lg"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500 mb-0.5">联系电话</p>
                        <p className="text-sm font-medium text-foreground-800">{aboutData.contact.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-secondary-100 text-secondary-600">
                        <i className="ri-map-pin-line text-lg"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500 mb-0.5">公司地址</p>
                        <p className="text-sm font-medium text-foreground-800">{aboutData.contact.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-background-200 text-foreground-500">
                        <i className="ri-time-line text-lg"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500 mb-0.5">工作时间</p>
                        <p className="text-sm font-medium text-foreground-800">{aboutData.contact.workHours}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
                  <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-5">
                    发送合作咨询
                  </h3>

                  {formSuccess ? (
                    <div className="text-center py-12">
                      <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-check-line text-2xl"></i>
                      </span>
                      <h4 className="font-heading font-bold text-xl text-foreground-900 mb-2">提交成功</h4>
                      <p className="text-sm text-foreground-500">感谢您的咨询，我们将在1-2个工作日内与您联系。</p>
                    </div>
                  ) : (
                    <form
                      onSubmit={handleSubmit}
                      className="flex flex-col gap-4"
                     
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            姓名 <span className="text-red-400">*</span>
                          </label>
                          <input
                            id="name"
                            name="name"
                            type="text"
                            required
                            className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            公司名称
                          </label>
                          <input
                            id="company"
                            name="company"
                            type="text"
                            className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                            placeholder="请输入您的公司名称"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            电子邮箱 <span className="text-red-400">*</span>
                          </label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                            placeholder="请输入您的电子邮箱"
                          />
                        </div>
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            联系电话
                          </label>
                          <input
                            id="phone"
                            name="phone"
                            type="tel"
                            className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                            placeholder="请输入您的联系电话"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="cooperation_type" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          合作类型 <span className="text-red-400">*</span>
                        </label>
                        <select
                          id="cooperation_type"
                          name="cooperation_type"
                          required
                          className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors cursor-pointer"
                        >
                          <option value="">请选择合作类型</option>
                          <option value="advertising">广告投放合作</option>
                          <option value="content">内容合作与授权</option>
                          <option value="platform">平台接入合作</option>
                          <option value="event">赛事活动合作</option>
                          <option value="other">其他合作</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          合作说明 <span className="text-red-400">*</span>
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors resize-none"
                          placeholder="请简要描述您的合作意向和需求（500字以内）"
                        />
                        <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                      </div>

                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="form-honeypot"
                      />

                      {formError && (
                        <p className="text-sm text-red-500">{formError}</p>
                      )}

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold transition-all duration-200 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                      >
                        {submitting ? '提交中...' : '提交合作咨询'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}